import { useModal } from '@monorepo/shared/hooks/useModalV2';
import { bodySmall } from '@monorepo/shared/styles/text';
import {
  FormTemplateFieldResponse,
  FormTemplateFieldTypes,
} from 'mapistry-shared';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { EditFieldModal } from './EditFieldModal/EditFieldModal';
import { hasForbiddenHtml } from './helperTextUtil';
import { ReorderableFormTemplateFieldsList } from './ReorderableFormTemplateFieldsList';

type EditFormTemplateSectionFieldsProps = {
  orderedFields: FormTemplateFieldResponse[];
  updateFieldOrder: (orderedFieldIds: string[]) => void;
  updateField: (field: FormTemplateFieldResponse) => void;
};

const Instructions = styled('span')`
  ${bodySmall}
`;

export function EditFormTemplateSectionFields({
  orderedFields,
  updateFieldOrder,
  updateField,
}: EditFormTemplateSectionFieldsProps) {
  const { confirm } = useModal();
  const [currentlyOpenFieldId, setCurrentlyOpenFieldId] = useState<
    string | undefined
  >();
  const closeFieldModal = useCallback(() => {
    setCurrentlyOpenFieldId(undefined);
  }, [setCurrentlyOpenFieldId]);
  const currentlyOpenField = orderedFields.find(
    (f) => f.id === currentlyOpenFieldId,
  );

  /*
   determinesWhetherOtherFieldsAreConditionallyShownMap is a dict that holds a true value for each
   fieldId that is the basis of another field being conditionally shown.
   This is then passed down the chain so we can disable option editing on these fields.
  */
  const determinesWhetherOtherFieldsAreConditionallyShownMap = useMemo(
    () =>
      orderedFields.reduce<Record<string, boolean>>((acc, field) => {
        if (field.conditionalFieldId) {
          return { ...acc, [field.conditionalFieldId]: true };
        }
        return acc;
      }, {}),
    [orderedFields],
  );

  const checkForForbiddenHtml = useCallback(
    async (originalField: FormTemplateFieldResponse) => {
      if (
        originalField.type !== FormTemplateFieldTypes.EXPLANATION_AREA ||
        !hasForbiddenHtml(originalField.helpText)
      ) {
        return true;
      }

      const confirmSave = await confirm({
        title: 'Saving will overwrite unsupported styles',
        description: (
          <>
            This explanation area was initially built by the Mapistry customer
            experience team and contains style elements unsupported on the
            mobile app, such as font color, font size, tables, etc. If you save
            your changes, these style elements will be erased. Are you sure you
            want to save your changes?
          </>
        ),
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Save',
        danger: true,
      });

      return confirmSave;
    },
    [confirm],
  );

  const onSubmitField = useCallback(
    async (updatedField: FormTemplateFieldResponse) => {
      const originalField = orderedFields.find(
        (field) => field.id === updatedField.id,
      );

      if (!originalField) return; // for typescript

      const shouldContinue = await checkForForbiddenHtml(originalField);
      if (!shouldContinue) return;

      updateField(updatedField);
      closeFieldModal();
    },
    [checkForForbiddenHtml, closeFieldModal, orderedFields, updateField],
  );

  const determinesWhetherOtherFieldsAreConditionallyShown =
    !!currentlyOpenFieldId &&
    !!determinesWhetherOtherFieldsAreConditionallyShownMap[
      currentlyOpenFieldId
    ];

  return (
    <div>
      <Instructions>Reorder or edit the fields in this section.</Instructions>
      {orderedFields && (
        <ReorderableFormTemplateFieldsList
          openEditFieldModal={setCurrentlyOpenFieldId}
          sortedFields={orderedFields}
          updateFieldOrder={updateFieldOrder}
        />
      )}
      {!!currentlyOpenField && (
        <EditFieldModal
          isOpen={!!currentlyOpenFieldId}
          onClose={closeFieldModal}
          onSubmit={onSubmitField}
          field={currentlyOpenField}
          determinesWhetherOtherFieldsAreConditionallyShown={
            determinesWhetherOtherFieldsAreConditionallyShown
          }
        />
      )}
    </div>
  );
}
